import { useCallback, useEffect, useRef } from "react";
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH } from "./useSidebarWidth.js";

/**
 * Vertical drag handle that lets the user widen or narrow the sidebar.
 * Width is owned by the parent via `useSidebarWidth()`; this component
 * just translates pointer drags into onChange calls. Uses pointer
 * events so mouse / trackpad / pen all behave identically.
 *
 * During a drag we set cursor: col-resize on <body> so the cursor
 * doesn't flicker back to text/default as it crosses neighbouring
 * panes. Cleaned up on pointerup/cancel and on unmount.
 */
export default function SidebarResizer({ onChange, disabled = false }) {
  const draggingRef = useRef(false);
  // Captured at drag start so we can compute width from the aside's
  // actual left edge rather than assuming the sidebar starts at x=0
  // (would break if the layout ever adds a left gutter).
  const asideLeftRef = useRef(0);
  const handleRef = useRef(null);

  // Stable-identity callbacks stored in refs so the cleanup path can
  // remove the exact same listeners that were added on pointerdown,
  // and so endDrag can reference itself without tripping the
  // "used before declared" lint rule.
  const onPointerMoveRef = useRef(null);
  const endDragRef = useRef(null);

  const onPointerMove = useCallback((e) => {
    if (!draggingRef.current) return;
    const raw = e.clientX - asideLeftRef.current;
    const next = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, raw));
    onChange?.(next);
  }, [onChange]);

  const endDrag = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("pointermove", onPointerMoveRef.current);
    window.removeEventListener("pointerup", endDragRef.current);
    window.removeEventListener("pointercancel", endDragRef.current);
  }, []);

  // Keep the refs in sync with the latest callback identities.
  onPointerMoveRef.current = onPointerMove;
  endDragRef.current = endDrag;

  const startDrag = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    // The <aside> is the resizer's previous flex sibling — grab its
    // left edge so drags compute "width = pointer.x − aside.left".
    const aside = handleRef.current?.previousElementSibling;
    asideLeftRef.current = aside ? aside.getBoundingClientRect().left : 0;
    draggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onPointerMoveRef.current);
    window.addEventListener("pointerup", endDragRef.current);
    window.addEventListener("pointercancel", endDragRef.current);
  }, [disabled]);

  // Clean up any leaked listeners on unmount (e.g. if the resizer
  // re-mounts mid-drag, or React fast-refresh kicks in during dev).
  useEffect(() => () => endDragRef.current?.(), []);

  if (disabled) return null;

  return (
    <div
      ref={handleRef}
      className="sidebar-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      onPointerDown={startDrag}
    />
  );
}
